import { DealerAddressType } from 'components/common/CommonTypes';
import { CommonValidation } from 'components/common/commonValidation/commonValidation';
import DealerAddress from 'components/common/dealerAddress/DealerAddress';
import DealerAddressLookup from 'components/common/dealerAddress/DealerAddressLookup';
import ErrorSummary from 'components/common/ErrorSummary/ErrorSummary';
import FormInput from 'components/common/FormInput';
import NumberInput from 'components/common/numberInput/NumberInput';
import { TyreRepairRequestContext } from 'context/TyreRepairRequestContext';
import i18next, { t } from 'i18next';
import React, { useContext, useEffect, useRef } from 'react';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import AddressManager from 'components/common/address/AddressManager';
import { GoogleAnalytics } from 'classes/GoogleAnalytics';
import { PageNames } from 'Constants/Common';

type TyreGeneralDetailsProps = {
    cancelConfirmText: string;
};

const TyreGeneralDetails = ({
    cancelConfirmText
}: TyreGeneralDetailsProps): JSX.Element => {
    const modalAddress = useRef(null);

    const {
        tyreRepairRequest,
        onChangeMileage,
        onDealerAddressUpdate,
        onCancelClick,
        onContinueRepairRequest,
        onCancelRepairRequest,
        onCancelUndo,
        onRepairAddressSelectionChanged
    } = useContext(TyreRepairRequestContext);

    const {
        scrollToAddress,
        selectedTyres,
        showCancelAlert,
        repairAddressSelectedIndex,
        repairAddresses
    } = tyreRepairRequest;

    const errorlist = (): Array<string | undefined> => {
        const values = [
            tyreRepairRequest.tyreFormErrors?.mileage,
            tyreRepairRequest.tyreFormErrors?.noRepairAddressSelected
        ];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };

    const onChangeTyreRequestMileage = (
        e: React.FormEvent<HTMLInputElement>
    ): void => {
        const value = (e.target as HTMLInputElement).value;
        if (CommonValidation.isEmpty(value)) {
            onChangeMileage('');
        } else if (CommonValidation.isInteger(value)) {
            onChangeMileage(value);
        }
    };

    const onDealerAddressUpdated = (dealerAddress: DealerAddressType): void => {
        onDealerAddressUpdate(dealerAddress);
    };

    const cancelButtonText =
        selectedTyres.length > 1
            ? t('cancel_repair_requests')
            : t('cancel_repair_request');

    useEffect(() => {
        if (scrollToAddress && modalAddress.current) {
            modalAddress.current.focus();
        }
        GoogleAnalytics.setPageView(PageNames.AlloyGeneral);
    }, []);

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='mileage-forminput'
                        label={t('alloy_repair_request.section_2_title')}
                        errorText={tyreRepairRequest.tyreFormErrors?.mileage}>
                        <div className='row repair-request-field-container'>
                            <div className='col-xs-12 col-lg-6'>
                                <NumberInput
                                    name='approx-mileage'
                                    value={
                                        tyreRepairRequest?.mileage.toString() ||
                                        ''
                                    }
                                    onChange={onChangeTyreRequestMileage}
                                    decimalPlaces={0}
                                />
                            </div>
                        </div>
                    </FormInput>
                    <FormInput
                        id='find-dealer-forminput'
                        label={t('find_dealer')}>
                        <DealerAddressLookup
                            dealerAddress={tyreRepairRequest?.dealerAddress}
                            onDealerAddressUpdated={onDealerAddressUpdated}
                        />
                        {tyreRepairRequest?.dealerAddress
                            ?.dealerStreetAddress && (
                            <DealerAddress
                                address={tyreRepairRequest?.dealerAddress}
                            />
                        )}
                    </FormInput>
                    <FormInput
                        id='repair-address-forminput'
                        label={t('alloy_repair_request.section_3_title')}
                        errorText={
                            tyreRepairRequest.tyreFormErrors
                                ?.noRepairAddressSelected
                        }>
                        <div className='ml-3'>
                            <p>{t('alloy_repair_request.section_3_para_1')}</p>
                            {i18next.language.toLocaleLowerCase() !== 'us' && (
                                <p>
                                    {t('alloy_repair_request.section_3_para_2')}
                                </p>
                            )}
                        </div>
                        <AddressManager
                            addressSelectedIndex={repairAddressSelectedIndex}
                            addresses={repairAddresses}
                            onAddressSelectionChanged={
                                onRepairAddressSelectionChanged
                            }
                        />
                        <div tabIndex={0} ref={modalAddress}></div>
                    </FormInput>
                </div>
                <div className='card-footer page-content-footer'>
                    {tyreRepairRequest.tyreFormErrors && (
                        <ErrorSummary
                            title={t('tyre_general_errors')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    id='btnContinueGeneral'
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onContinueRepairRequest}>
                    {t('button_label.continue')}
                </button>
                <button
                    id='btnCancelGeneral'
                    data-testid='danger-button'
                    className='btn btn-danger progress-button'
                    onClick={onCancelClick}>
                    {cancelButtonText}
                </button>
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
        </>
    );
};

export default TyreGeneralDetails;
